import React, { useEffect, useState } from 'react';
import useMount from 'react-use/lib/useMount';
import { RouteComponentProps, Redirect } from 'react-router';
import _ from 'lodash';
import qs from 'qs';
import { useHistory } from 'react-router-dom';
import { GetUrlParams } from '@tradingblock/api';
import { IStorageEngine } from '@tradingblock/types';
import { useLocalStorage, TokenFromStorage, SaveTokenInStorage } from '@tradingblock/storage';
import Config from '../config';
import { useDispatcher } from '../data/global/hooks';
import { useStateSelector, refreshTokenSelector } from '../data/global/dataSelectors';
import { Actions } from '../data/global/actions';
import { validateToken, verifyToken, tokenCheck, extractApiTokenFromJwt } from '../utilities/auth';
import { LoginRedirectUrl } from '../utilities/url';
import { usePageSettings } from '../framework/WrappedRoute';

const tryGetToken = async (
  storage: IStorageEngine,
  tokenFromUrl: string | undefined,
  tokensFromState: { token: string | undefined }
) => {
  const tokenFromLS = await TokenFromStorage(storage);
  const tokenVal = tokenFromUrl || tokenFromLS;
  const { token } = tokensFromState;

  if (!tokenVal && !token) {
    console.warn('token not found from url or local storage');
    return null;
  }

  // For URL tokens, first check with TokenCheck before proceeding (skip for VT)
  if (tokenFromUrl && !Config.isVirtual) {
    // Extract API token from JWT
    const extractedApiToken = extractApiTokenFromJwt(tokenFromUrl);
    const apiToken = extractedApiToken || tokenFromUrl;

    const tokenCheckResult = await tokenCheck(apiToken);
    if (!tokenCheckResult) {
      console.warn('TokenCheck failed for URL token');
      return null;
    }
  }

  // const tokenResult = tokenVal ? await validateToken(tokenVal) : token ? await verifyToken(token) : undefined;
  const tokenResult = tokenVal ? await verifyToken(tokenVal) : token ? await verifyToken(token) : undefined;
  if (!tokenResult) {
    console.warn('token found was invalid');
    return null;
  }
  const { value, decoded } = tokenResult;
  const tokenPayload = { ...decoded, apiToken: decoded.token, token: value };
  await SaveTokenInStorage(storage, value);
  return tokenPayload;
};

const tryGetOrigin = async (storage: IStorageEngine, originFromUrl: string, originFromState: string) => {
  const originValue = originFromUrl;
  const originFromLS = localStorage.getItem('origin');
  let parseOriginLS = '';

  if (typeof originFromLS === 'string') {
    parseOriginLS = JSON.parse(originFromLS);
  }

  if (!originValue && !originFromLS) {
    console.warn('origin found was invalid');
    return undefined;
  }

  if (!originValue) {
    return parseOriginLS;
  }
  localStorage.setItem('origin', JSON.stringify(originValue));
  return originValue;
};

export const AuthenticationPage: React.FC<RouteComponentProps<{}>> = ({ location }) => {
  usePageSettings({ name: 'Authentication', access: 'public' });
  const { dispatch } = useDispatcher();
  const urlParams = qs.parse(_.drop(location.hash, 1).join(''));

  const tokenFromUrl = _.isString(urlParams.id_token) ? (urlParams.id_token as string) : undefined;
  const storage = useLocalStorage();
  const tokensFromState = useStateSelector(refreshTokenSelector);
  const originFromState = useStateSelector(s => s.auth.origin);
  const isExpired = useStateSelector(s => s.auth.isExpired);
  const params = GetUrlParams(location.search);
  const nextPageFromUrlSearchParams = decodeURIComponent(params['next'] || '/');
  const grp = process.env.REACT_APP_TB_SITE_GROUP;
  const urlSearchParamsFromNextPage = GetUrlParams(nextPageFromUrlSearchParams);
  const nextPagePathName: string = nextPageFromUrlSearchParams.split('?')[0] || '/';
  const history = useHistory();

  let nextPageSearchParams: string | undefined;

  if (Object.keys(urlSearchParamsFromNextPage).length > 0) {
    const urlSearchParams = new URLSearchParams();

    if (urlSearchParamsFromNextPage['showAccountUpdateModal']) {
      urlSearchParams.append('showAccountUpdateModal', urlSearchParamsFromNextPage['showAccountUpdateModal']);
    }

    if (urlSearchParamsFromNextPage['accountHolderIndex']) {
      urlSearchParams.append('accountHolderIndex', urlSearchParamsFromNextPage['accountHolderIndex']);
    }

    nextPageSearchParams = urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : '';
  }

  const [currentToken, setCurrentToken] = useState<string | null>();

  useMount(() =>
    tryGetToken(storage, tokenFromUrl, tokensFromState)
      .then(tokenRes => {
        if (tokenRes) {
          // if account is pending, send to "no account" page
          if (tokenRes.pending) {
            history.push(`/no-account?grp=${grp}`);
            return;
          }
          dispatch(Actions.receiveToken(tokenRes));
          if (window.location.pathname.includes('/auth')) {
            setCurrentToken(tokenRes.token);
          }
        } else {
          setCurrentToken(null);
        }
      })
      .catch(err => {
        console.log('token verification errored', err);
        setCurrentToken(null);
      })
  );

  useMount(() =>
    tryGetOrigin(storage, urlParams.grp, originFromState)
      .then(originRes => {
        if (originRes) {
          dispatch(Actions.setOrigin({ origin: originRes }));
        } else {
          return;
        }
      })
      .catch(err => {
        console.log('origin errored', err);
      })
  );

  useEffect(() => {
    if (isExpired === true) {
      setCurrentToken(null);
    }
  }, [isExpired]);

  useEffect(() => {
    if (currentToken === null) {
      // For external redirects to login service, the base URL already includes
      // the branch prefix (from env vars modified by pipeline). The 'next' parameter should be
      // a path relative to that service's basename, not include the branch prefix again.
      // The external service (login/apiserver) has its own BrowserRouter with basename.
      window.location.href = `${Config.loginUrl}?redirect=${LoginRedirectUrl}&next=/&grp=${grp}`;
    }
  }, [currentToken]);

  return (
    <div>
      {
        !_.isNil(currentToken) &&
        <Redirect
          to={{
            pathname: nextPagePathName,
            search: nextPageSearchParams,
          }}
        />
      }
    </div>
  );
};
